import React, { useState, useEffect } from "react";
import {
  Button,
  Input,
  Card,
  Alert,
  Row,
  Col,
  Skeleton,
  Tag,
  Form,
} from "antd";
import { useDispatch } from "react-redux";
import { useEmailWebSocket } from "../../common/hooks/useEmailWebSocket";
import {
  createAISession,
  generateEmailAI,
} from "../../actionCreator/genAIActionCreator";
import { getUserInfo } from "../../utils/utils";
import SidePanelEmailComposer from "./SidePanelEmailComposer";
import aiIcon from "../../assets/svg/icons/ai-assist.svg";
import { ReactComponent as SendIcon } from "../../assets/svg/icons/send-email.svg";

const { TextArea } = Input;

const extractAIFields = (raw = "") => {
  const subjMatch = raw.match(/<subject>([\s\S]*?)<\/subject>/i);
  const subject = subjMatch ? subjMatch[1].trim() : "";
  const body = subjMatch
    ? raw.replace(/[\s\S]*<\/subject>/i, "").trim()
    : raw.trim();
  return { subject, body };
};

const fixHtml = (html = "") =>
  html
    .replace(/\n/g, "<br/>")
    .replace(/ {2,}/g, (m) => "&nbsp;".repeat(m.length));

const DualEmailDraftPanel = ({ recipient }) => {
  const dispatch = useDispatch();
  const { id: userId } = getUserInfo();
  const [form] = Form.useForm();
  const [error, setError] = useState("");
  const [generating, setGenerating] = useState(false);
  const [selectedDraft, setSelectedDraft] = useState(null);

  const [drafts, setDrafts] = useState([
    { raw: "", subject: "", body: "", loading: false, done: false },
    { raw: "", subject: "", body: "", loading: false, done: false },
  ]);

  const ws = useEmailWebSocket("email-channel-1");

  const STREAM_IDLE_MS = 1200;
  const STREAM_MAX_MS = 30000;

  useEffect(() => {
    return () => {
      ws.unsubscribeFromSession();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateDraft = async (
    index,
    intent,
    sessionIdOverride,
    options = {},
  ) => {
    setDrafts((prev) => {
      const updated = [...prev];
      updated[index] = {
        raw: "",
        subject: "",
        body: "",
        loading: true,
        done: false,
      };
      return updated;
    });

    let idleTimer = null;
    let hardTimer = null;
    try {
      let sessionId = sessionIdOverride;
      if (!sessionId) {
        const sessionRes = await dispatch(createAISession());
        sessionId = sessionRes?.data?.sessionId;
      }

      if (!sessionId) {
        setDrafts((prev) => {
          const updated = [...prev];
          updated[index] = { ...updated[index], loading: false, done: true };
          return updated;
        });
        setError("Unable to create AI session for draft " + (index + 1));
        return;
      }

      let resolveIdle;
      const idlePromise = new Promise((resolve) => {
        resolveIdle = resolve;
      });

      const markActivity = () => {
        if (idleTimer) clearTimeout(idleTimer);
        idleTimer = setTimeout(() => {
          resolveIdle();
        }, STREAM_IDLE_MS);
      };

      hardTimer = setTimeout(() => {
        resolveIdle();
      }, STREAM_MAX_MS);

      await ws.subscribeToSession(sessionId, (chunk) => {
        setDrafts((prev) => {
          const updated = [...prev];
          const newRaw = updated[index].raw + chunk;
          const parsed = extractAIFields(newRaw);
          updated[index] = {
            ...updated[index],
            raw: newRaw,
            subject: parsed.subject,
            body: parsed.body,
          };
          return updated;
        });
        markActivity();
      });

      const user = getUserInfo();
      const payloadObj = {
        sessionId,
        userInput: intent,
        senderFullName:
          `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
        lead: {
          firstName: recipient?.firstname,
          lastName: recipient?.lastname,
          title: recipient?.title,
          companyName: recipient?.companyName,
        },
      };

      await dispatch(generateEmailAI(payloadObj));

      if (options.waitForIdle) {
        await idlePromise;
      }
    } catch (err) {
      console.error("[DualEmailDraftPanel] Draft", index + 1, "failed:", err);
    } finally {
      if (idleTimer) clearTimeout(idleTimer);
      if (hardTimer) clearTimeout(hardTimer);
      ws.unsubscribeFromSession();
      setDrafts((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], loading: false, done: true };
        return updated;
      });
    }
  };

  const handleGenerate = async (values) => {
    if (!values?.emailIntent.trim()) {
      setError("Please describe what this email is about.");
      return;
    }
    setError("");
    setSelectedDraft(null);
    setGenerating(true);
    try {
      if (values?.isSingle) {
        await Promise.all([
          generateDraft(0, values?.emailIntent, undefined, {
            waitForIdle: true,
          }),
        ]);
      } else {
        const sessionRes = await dispatch(createAISession());
        const sessionId = sessionRes?.data?.sessionId;
        if (!sessionId) {
          setError("Unable to create AI session for drafts");
          return;
        }
        await generateDraft(0, values?.emailIntent, sessionId, {
          waitForIdle: true,
        });
        await generateDraft(1, values?.emailIntent, sessionId, {
          waitForIdle: true,
        });
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleReset = () => {
    ws.unsubscribeFromSession();
    setDrafts([
      { raw: "", subject: "", body: "", loading: false, done: false },
      { raw: "", subject: "", body: "", loading: false, done: false },
    ]);
    setGenerating(false);
    setSelectedDraft(null);
    setError("");
  };

  const handleSelectDraft = (draft) => {
    setSelectedDraft({
      subject: draft.subject,
      body: fixHtml(draft.body),
    });
  };

  const recipientLabel = `${recipient?.firstname || ""} ${recipient?.lastname || ""} (${recipient?.emailId || ""})`;
  const hasDrafts = drafts.some((d) => d.raw || d.loading);

  return (
    <>
      <div
        className={`${selectedDraft ? "tw-flex" : "tw-hidden"} tw-items-center tw-flex-col tw-justify-center`}
      >
        <div className="tw-max-w-[610px] tw-w-full tw-border tw-border-solid tw-rounded-[10px] tw-border-border-secondary">
          <div className="tw-py-2 tw-px-4 tw-text-sm tw-rounded-[10px_10px_0_0] tw-bg-secondary-color tw-border-b tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-border-secondary">
            Compose Email
          </div>
          <SidePanelEmailComposer
            recipientEmail={recipient?.emailId}
            empId={userId}
            selectedDraft={selectedDraft}
            payload={{ ...recipient, ...selectedDraft }}
            callback={() => setSelectedDraft(null)}
            handleReset={handleReset}
          />
        </div>
      </div>
      {!selectedDraft && (
        <>
          {!hasDrafts && (
            <>
              <div className="tw-border tw-border-solid tw-rounded-[10px] tw-border-border-secondary">
                <div className="tw-py-2 tw-px-4 tw-text-sm tw-rounded-[10px_10px_0_0] tw-bg-secondary-color tw-border-b tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-border-secondary">
                  Draft email to{" "}
                  <span className="tw-text-theme tw-font-medium">
                    {recipient?.emailId}
                  </span>
                </div>

                <Form form={form} onFinish={handleGenerate}>
                  <div className="tw-px-4 tw-py-3">
                    <div>
                      To:{" "}
                      <span className="tw-text-theme tw-font-medium ">
                        {recipient?.emailId}
                      </span>
                    </div>

                    <Form.Item
                      name="emailIntent"
                      rules={[
                        {
                          required: true,
                          message: "Please enter email intent",
                        },
                        {
                          validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            if (/^\s/.test(value)) {
                              return Promise.reject(
                                new Error(
                                  "Email intent cannot start with a space",
                                ),
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <TextArea
                        placeholder="E.g., Follow up on our meeting, Welcome to the platform..."
                        rows={4}
                        className="tw-rounded-md tw-mt-4 tw-border-[#9787FE]"
                        style={{
                          background:
                            "linear-gradient(180deg, #F2F0FF 0%, #FFFFFF 50%, #F2F0FF 100%)",
                        }}
                      />
                    </Form.Item>

                    <div className="tw-flex tw-justify-end tw-mt-4">
                      <Button
                        type="primary"
                        htmlType="submit"
                        disabled={generating}
                        loading={generating}
                        className="!tw-bg-theme tw-font-medium !tw-outline-0 !tw-border-theme !tw-text-white tw-font-medium tw-text-sm"
                      >
                        Generate 2 Draft Variations
                      </Button>
                    </div>
                  </div>
                </Form>
              </div>
              <div className="tw-flex tw-flex-col tw-items-center tw-justify-center">
                <div className="tw-text-[#A1A1A1] tw-text-sm tw-my-10">
                  ----------------- or -----------------
                </div>
                <Button
                  className="tw-w-fit !tw-bg-secondary-color !tw-border-border-secondary tw-text-sm tw-font-medium !tw-text-text-primary"
                  onClick={() => setSelectedDraft({})}
                >
                  <SendIcon /> Custom email
                </Button>
              </div>
            </>
          )}
          {/* DUAL DRAFT CARDS */}
          {hasDrafts && (
            <>
              <div className="tw-flex tw-justify-between tw-items-center tw-mb-3">
                <span className="tw-text-sm tw-font-semibold tw-text-text-primary">
                  2 AI Draft variations - pick one to edit &amp; Send
                </span>
                <Button
                  className="!tw-bg-secondary-color !tw-text-text-primary !tw-border-secondary-color tw-text-sm tw-font-medium"
                  onClick={handleReset}
                >
                  Start Over
                </Button>
              </div>

              <Row gutter={[12, 12]}>
                {drafts.map((draft, index) => (
                  <Col span={12} key={index}>
                    <div className="tw-p-0 tw-h-full tw-border tw-border-solid tw-rounded-[10px] tw-border-border-secondary">
                      <div className="tw-rounded-[10px_10px_0_0] tw-flex tw-items-center tw-justify-between tw-bg-secondary-color tw-border-b tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-border-secondary tw-py-2 tw-px-4">
                        <span className="tw-text-sm tw-font-medium">
                          Draft {index + 1}
                        </span>
                        {draft.loading && (
                          <Tag color="processing" className="tw-text-[10px]">
                            Generating...
                          </Tag>
                        )}
                        {draft.done && (
                          <Tag
                            color="#E4FFDB"
                            className="tw-text-[10px] !tw-text-[#30C303]"
                          >
                            Ready
                          </Tag>
                        )}
                      </div>
                      <div className="tw-py-4 tw-px-4 tw-flex tw-flex-col tw-justify-between">
                        {draft.loading && !draft.raw && (
                          <div className="tw-flex tw-flex-col tw-gap-2">
                            <Skeleton.Input
                              active
                              block
                              style={{ height: 14 }}
                            />
                            <Skeleton.Input
                              active
                              block
                              style={{ height: 14, width: "80%" }}
                            />
                            <Skeleton.Input
                              active
                              block
                              style={{ height: 14 }}
                            />
                            <Skeleton.Input
                              active
                              block
                              style={{ height: 14, width: "90%" }}
                            />
                            <Skeleton.Input
                              active
                              block
                              style={{ height: 14, width: "70%" }}
                            />
                          </div>
                        )}

                        {!!draft.raw && (
                          <div style={{ maxHeight: 220, overflowY: "auto" }}>
                            {draft.subject ? (
                              <div className="tw-font-bold tw-text-xs tw-mb-2">
                                {draft.subject}
                              </div>
                            ) : (
                              <div className="tw-text-gray-400 tw-italic tw-text-xs tw-mb-2">
                                Generating subject...
                              </div>
                            )}
                            <div
                              className="tw-text-xs tw-text-gray-700 tw-leading-relaxed"
                              dangerouslySetInnerHTML={{
                                __html: fixHtml(draft.body),
                              }}
                            />
                          </div>
                        )}

                        {draft.done && draft.raw && (
                          <div className="tw-text-center">
                            <Button
                              type="primary"
                              className="tw-mt-3 !tw-text-theme tw-text-sm tw-font-medium !tw-bg-[#F1EFFF] !tw-border-[#F1EFFF] tw-w-fit"
                              onClick={() => handleSelectDraft(draft)}
                            >
                              <img src={aiIcon} alt="" /> Use & Personalised
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </>
          )}
        </>
      )}
    </>
  );
};

export default DualEmailDraftPanel;
